import BaseModal from '@/components/BaseModal';
import BoldSmallText from '@/components/BoldSmallText';
import CustomLayoutGeneralSettings from '@/components/CustomLayoutGeneralSettings';
import MediumText from '@/components/MediumText';
import RegularText from '@/components/RegularText';
import UpgradeTextWithTouchPoint from '@/components/UpgradeTextWithTouchPoint';
import AutoCompleteWithTag from '@/components/autoCompleteWithTag';
import { BREAKPOINT, CountryCode } from '@/constants/enum';
import { googleCountryOptions } from '@/constants/options';
import { stringify } from '@/helpers/string';
import { formatDate, getSomeDaysAgo } from '@/helpers/time';
import useUserPlan from '@/hooks/useUserPlan';
import { blackoutHolidaysApiCaller, locationApiCaller } from '@/redux/query';
import settingsSlice, { dataSettingsSelector } from '@/redux/slice/settings.slice';
import { IOptions } from '@/types';
import {
  Button,
  DatePicker,
  Divider,
  Icon,
  InlineError,
  Collapsible as PolarisCollapsible,
  Popover,
  Select,
  Tag,
} from '@shopify/polaris';
import { CalendarIcon, GlobeIcon } from '@shopify/polaris-icons';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { BlackoutDateStyled } from './styled';
import { useMediaQuery } from 'react-responsive';
const now = new Date();

// Move these calculations outside the component
const createOptionsWithSelectAll = (availableHolidayOptions: IOptions[]) => [
  { label: 'Select all dates', value: 'all' },
  ...availableHolidayOptions,
];

// Memoize complex calculations
const getSelectedOptions = (isNotSelectedAllHolidayOptions: boolean, specificDayOff: string[]) =>
  isNotSelectedAllHolidayOptions ? specificDayOff : ['all', ...specificDayOff];

const BlackoutDate = ({ disableCardBorder }: { disableCardBorder?: boolean }) => {
  const dispatch = useDispatch();
  const { isFreePlan } = useUserPlan();
  const isMobile = useMediaQuery({ maxWidth: BREAKPOINT.SM });
  const dataSettings = useSelector(dataSettingsSelector);
  const [selectedCountry, setSelectedCountry] = useState<string>('');
  const { data: locationInfo } = locationApiCaller.useCheckIpLocationQuery();
  const { data: blackoutHolidays } = blackoutHolidaysApiCaller.useGetBlackoutHolidaysQuery(
    {
      year: now.getFullYear(),
      countryCode: CountryCode[selectedCountry as keyof typeof CountryCode],
    },
    { skip: !selectedCountry || isFreePlan },
  );

  useEffect(() => {
    if (!selectedCountry) {
      setSelectedCountry(locationInfo?.data?.country_code?.toLocaleLowerCase() || '');
    }
  }, [locationInfo?.data?.country_code, selectedCountry]);

  const availableHolidayOptions = useMemo(() => {
    const holidays =
      blackoutHolidays?.items
        .map((item) => {
          const value = item.start.date;
          const isInThePast = new Date(item.start.date) < new Date();
          if (isInThePast) {
            return null;
          }
          return {
            label: item.summary + ' - ' + formatDate(value, 'D MMM YYYY'),
            value: formatDate(value, 'M/D/YYYY'),
            disabled: isInThePast,
          };
        })
        .filter((item) => !!item) || [];
    return holidays.sort((a, b) => new Date(a.value).getTime() - new Date(b.value).getTime());
  }, [blackoutHolidays?.items]);

  const [isOpenDatePicker, setIsOpenDatePicker] = useState(false);
  const [isSelectPast, setIsSelectPast] = useState(false);
  const [isConfirmDeleteAll, setIsConfirmDeleteAll] = useState(false);
  const [date, setDate] = useState({
    month: now.getMonth(),
    year: now.getFullYear(),
  });

  const onRemove = (index: number) => () => {
    if (dataSettings) {
      const newSelectedDates = [...dataSettings.specific_day_off];
      newSelectedDates.splice(index, 1);
      dispatch(
        settingsSlice.actions.handleDataSettings({
          ...dataSettings,
          specific_day_off: newSelectedDates,
        }),
      );
    }
  };

  const tagSelectedOptions = useMemo(() => {
    const tags = dataSettings?.specific_day_off?.map((item) => {
      const itemExitsInHolidayOptions = availableHolidayOptions?.find((itemHoliday) => itemHoliday.value === item);
      if (itemExitsInHolidayOptions) {
        return itemExitsInHolidayOptions;
      }
      return { label: '', value: item };
    });

    return tags;
  }, [dataSettings?.specific_day_off, availableHolidayOptions]);

  const isNotSelectedAllHolidayOptions = useMemo(() => {
    const allHolidayOptions = availableHolidayOptions.map((item) => item.value);
    return allHolidayOptions?.some((item) => {
      return !dataSettings?.specific_day_off.includes(item);
    });
  }, [availableHolidayOptions, dataSettings?.specific_day_off]);

  const memoizedOptions = useMemo(() => createOptionsWithSelectAll(availableHolidayOptions), [availableHolidayOptions]);

  const handleSelectedOptionsChange = useCallback(
    (values: IOptions[]) => {
      const isSelectAll = values.some((item) => item.value === 'all');
      const allHolidayValues = availableHolidayOptions.map((item) => item.value);

      let newDayOffs: string[];
      if (isSelectAll && isNotSelectedAllHolidayOptions) {
        newDayOffs = Array.from(new Set([...dataSettings?.specific_day_off, ...allHolidayValues]));
      } else if (!isSelectAll && !isNotSelectedAllHolidayOptions) {
        newDayOffs = dataSettings?.specific_day_off?.filter((dayOff) => !allHolidayValues.includes(dayOff));
      } else {
        newDayOffs = values.filter((item) => item.value !== 'all').map((item) => item.value);
      }

      dispatch(
        settingsSlice.actions.handleDataSettings({
          ...dataSettings,
          specific_day_off: newDayOffs,
        }),
      );
    },
    [availableHolidayOptions, dataSettings, dispatch, isNotSelectedAllHolidayOptions],
  );

  const memoizedSelectedOptions = useMemo(
    () => getSelectedOptions(isNotSelectedAllHolidayOptions, dataSettings?.specific_day_off),
    [isNotSelectedAllHolidayOptions, dataSettings?.specific_day_off],
  );

  return (
    <BlackoutDateStyled>
      <CustomLayoutGeneralSettings
        title="Blackout date (holiday)"
        description={
          disableCardBorder ? (
            <MediumText>These dates will be excluded from the estimation.</MediumText>
          ) : (
            <RegularText>Exclude specific dates from estimation</RegularText>
          )
        }
        isShowDivider
      >
        <div>
          <div className="w-full">
            <div className={`d-flex mb-16 ${isMobile ? 'flex-column' : 'flex-row justify-between'}`}>
              <div>
                <BoldSmallText className={`${isFreePlan ? 'text--gray' : ''}`}>Public holidays</BoldSmallText>
                <div>
                  <p className={`${isFreePlan ? 'text--gray' : ''}`}>
                    Quickly exclude available public holidays in your operated markets
                  </p>
                </div>
              </div>
              <div className="min-w-fit">
                <UpgradeTextWithTouchPoint text="Standard or above" />
              </div>
            </div>
            <Select
              options={googleCountryOptions.map((item) => ({ ...item, prefix: <Icon source={GlobeIcon} tone="subdued" /> }))}
              onChange={(value) => setSelectedCountry(value)}
              value={selectedCountry}
              label="Select Country"
              labelHidden
              disabled={isFreePlan}
            />

            <div className="mt-16">
              <AutoCompleteWithTag
                allowMultiple
                options={memoizedOptions}
                tag={tagSelectedOptions}
                label=""
                placeholder="Select holiday dates"
                selectedOptions={memoizedSelectedOptions}
                setSelectedOptions={handleSelectedOptionsChange}
                disable={isFreePlan}
                prefixIcon={CalendarIcon}
                isHideTagMarkup
              />
            </div>
          </div>

          <div className="mt-16 mb-16">
            <div className="mb-8">
              <BoldSmallText>Add manually</BoldSmallText>
            </div>
            <Popover
              sectioned
              captureOverscroll={false}
              active={isOpenDatePicker}
              activator={<Button onClick={() => setIsOpenDatePicker(!isOpenDatePicker)}>Select dates</Button>}
              onClose={() => {
                setIsOpenDatePicker(!isOpenDatePicker);
              }}
            >
              <DatePicker
                key={stringify(dataSettings?.specific_day_off)}
                onMonthChange={(month: number, year: number) => setDate({ month, year })}
                month={date.month}
                year={date.year}
                onChange={(value) => {
                  if (value.start > getSomeDaysAgo(1)) {
                    if (isSelectPast) {
                      setIsSelectPast(false);
                    }
                    const newDates = dataSettings.specific_day_off ? [...dataSettings.specific_day_off] : [];
                    newDates.push(value.start.toLocaleDateString('en-US'));
                    dispatch(settingsSlice.actions.handleDataSettings({ ...dataSettings, specific_day_off: newDates }));
                  } else {
                    setIsSelectPast(true);
                  }
                }}
                disableSpecificDates={
                  dataSettings?.specific_day_off ? dataSettings?.specific_day_off.map((item) => new Date(item)) : []
                }
              />
            </Popover>
          </div>
          <Divider />

          <div className="mt-8 d-flex">
            <b style={{ lineHeight: '28px' }}>Excluded dates</b>
            <div className="ml-8">
              <Button tone="critical" variant="tertiary" onClick={() => setIsConfirmDeleteAll(true)}>
                Clear all
              </Button>
            </div>
          </div>
          <PolarisCollapsible
            open={isSelectPast}
            id="specific-dates-collap"
            transition={{ duration: '300ms', timingFunction: 'ease-in-out' }}
          >
            <div className="mt-8">
              <InlineError message="Cannot select dates in the past" fieldID="specific-dates" />
            </div>
          </PolarisCollapsible>

          <div className="blackout-date-container">
            {dataSettings?.specific_day_off?.map((item, index) => {
              const itemTime = new Date(item);
              itemTime.setHours(0, 0, 0, 0);
              const nowTime = new Date();
              nowTime.setHours(0, 0, 0, 0);
              if (itemTime.getTime() < nowTime.getTime()) {
                return null;
              }
              return (
                <Tag onRemove={onRemove(index)} key={formatDate(item)}>
                  {formatDate(item, 'D MMM YYYY')}
                </Tag>
              );
            })}
          </div>
        </div>
      </CustomLayoutGeneralSettings>
      <BaseModal
        title="Are you sure you want to delete all?"
        open={isConfirmDeleteAll}
        onClose={() => {
          setIsConfirmDeleteAll(false);
        }}
        primaryAction={{
          content: 'Delete',
          //@ts-ignore
          tone: 'critical',
          onAction: () => {
            dispatch(settingsSlice.actions.handleDataSettings({ ...dataSettings, specific_day_off: [] }));
            setIsConfirmDeleteAll(false);
          },
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: () => {
              setIsConfirmDeleteAll(false);
            },
          },
        ]}
      >
        <RegularText>This action cannot be undone.</RegularText>
      </BaseModal>
    </BlackoutDateStyled>
  );
};

export default memo(BlackoutDate);
